if (!customElements.get('results-mobile-facets')) {
  customElements.define(
    'results-mobile-facets',
    class ResultsMobileFacets extends HTMLFormElement {
      constructor () {
        super()
        this.eventBus = window.theme.results.eventBus
        this.sortOptions = window.theme.results.stateLayer.state.sort
        this.lastFacets = null

        this.handleFormChangeBound = this.handleFormChange.bind(this)
        this.handleSortClickBound = this.handleSortClick.bind(this)
        this.handlePriceInputBound = this.handlePriceInput.bind(this)
        this.handleResetBound = this.handleReset.bind(this)
      }

      connectedCallback () {
        this.els = {
          list: this.querySelector('[data-results-mobile-facets-el="list"]'),
          form: this,
          activeFilters: this.querySelector('[data-results-mobile-facets-el="activeFilters"]'),
          activeList: this.querySelector('[data-results-mobile-facets-el="activeList"]'),
        }

        const EVENTS = window.theme.results.events

        this.onFacetsUpdated = ({ facets, activeFacets }) => {
          this.lastFacets = facets
          this.renderFacets(facets)
          this.renderActiveFacets(activeFacets)
        }
        this.onActiveFacetsUpdated = ({ activeFacets }) => {
          this.renderActiveFacets(activeFacets)
        }

        this.eventBus.subscribe(EVENTS.STATE.FACETS_UPDATED, this.onFacetsUpdated)
        this.eventBus.subscribe(EVENTS.STATE.ACTIVE_FACETS_UPDATED, this.onActiveFacetsUpdated)
      }

      disconnectedCallback () {
        const EVENTS = window.theme.results.events
        this.eventBus.unsubscribe(EVENTS.STATE.FACETS_UPDATED, this.onFacetsUpdated)
        this.eventBus.unsubscribe(EVENTS.STATE.ACTIVE_FACETS_UPDATED, this.onActiveFacetsUpdated)
      }

      setupEventListeners () {
        if (!this.formChangeBound) {
          this.els.form.addEventListener('change', this.handleFormChangeBound)
          this.formChangeBound = true
        }

        if (!this.listListenersBound) {
          this.els.list.addEventListener('click', this.handleSortClickBound)
          this.els.list.addEventListener('input', this.handlePriceInputBound)
          this.listListenersBound = true
        }

        if (!this.resetListenerBound) {
          const reset = document.querySelector(
            '[data-results-mobile-facets-external-element="reset"]',
          )

          if (reset) {
            reset.addEventListener('click', this.handleResetBound)
            this.resetListenerBound = true
          }
        }
      }

      handleCloseFacetAccordions (event) {
        this.querySelectorAll('details[open]').forEach(detail => {
          detail.removeAttribute('open')
        })
      }

      handleSortClick (event) {
        const button = event.target.closest('.clc-Sort_Facet')

        if (button) {
          const value = button.getAttribute('data-sort-value')
          const [field, order] = value.split('~')
          this.eventBus.publish(window.theme.results.events.UI.SORT_CHANGED, {
            field,
            order,
          })
        }
      }

      handleReset () {
        this.eventBus.publish(window.theme.results.events.UI.FACET_CLEAR_ALL)
      }

      handleFormChange (event) {
        const target = event.target

        if (target.type === 'number') return

        const value = [target.value]
        const field = target.getAttribute('data-facet-field')
        const UI = window.theme.results.events.UI

        if (target.checked === true) {
          this.eventBus.publish(UI.FACET_ADDED, { field, value })
        } else {
          this.eventBus.publish(UI.FACET_REMOVED, { field, value })
        }
      }

      handlePriceInput (event) {
        const target = event.target
        if (!target.matches('[data-facet-field="price"]')) return

        clearTimeout(this.priceDebounce)
        this.priceDebounce = setTimeout(() => {
          const priceRange = this.els.list.querySelector('[data-facet-price]')
          if (!priceRange) return

          const minEl = priceRange.querySelector('[data-price-range-el="min"]')
          const maxEl = priceRange.querySelector('[data-price-range-el="max"]')
          if (!minEl || !maxEl) return

          const boundsMin = Number.parseFloat(minEl.getAttribute('min'))
          const boundsMax = Number.parseFloat(maxEl.getAttribute('max'))

          const safeBoundsMin = Number.isNaN(boundsMin) ? 0 : boundsMin
          const safeBoundsMax = Number.isNaN(boundsMax)
            ? safeBoundsMin
            : Math.max(boundsMax, safeBoundsMin)

          const parsedMin = Number.parseFloat(minEl.value)
          const parsedMax = Number.parseFloat(maxEl.value)

          let normalizedMin = Number.isNaN(parsedMin) ? safeBoundsMin : parsedMin
          let normalizedMax = Number.isNaN(parsedMax) ? safeBoundsMax : parsedMax

          normalizedMin = Math.min(Math.max(normalizedMin, safeBoundsMin), safeBoundsMax)
          normalizedMax = Math.min(Math.max(normalizedMax, safeBoundsMin), safeBoundsMax)

          if (normalizedMin > normalizedMax) {
            if (target === minEl) {
              normalizedMax = normalizedMin
            } else {
              normalizedMin = normalizedMax
            }
          }

          minEl.value = String(normalizedMin)
          maxEl.value = String(normalizedMax)

          this.eventBus.publish(window.theme.results.events.UI.PRICE_CHANGED, {
            min: minEl.value,
            max: maxEl.value,
          })
        }, 400)
      }

      renderFacets (facets) {
        const openFields = new Set()
        let sortOpen = false
        let priceOpen = false

        this.els.list.querySelectorAll('details[open]').forEach(detail => {
          if (detail.hasAttribute('data-sort-accordion')) {
            sortOpen = true
            return
          }
          if (detail.hasAttribute('data-price-accordion')) {
            priceOpen = true
            return
          }
          const input = detail.querySelector('[data-facet-field]')
          if (input) openFields.add(input.getAttribute('data-facet-field'))
        })

        this.els.list.innerHTML = ''

        facets.forEach(facet => {
          this.els.list.insertAdjacentHTML(
            'beforeend',
            window.theme.results.templates.AccordionFacets(facet),
          )
        })

        this.els.list.insertAdjacentHTML(
          'beforeend',
          window.theme.results.templates.SortAccordion(this.sortOptions),
        )

        openFields.forEach(field => {
          const input = this.els.list.querySelector(`[data-facet-field="${field}"]`)
          if (input) {
            const details = input.closest('details')
            if (details) details.setAttribute('open', '')
          }
        })

        if (sortOpen) {
          const sortDetails = this.els.list.querySelector('details[data-sort-accordion]')
          if (sortDetails) sortDetails.setAttribute('open', '')
        }

        if (priceOpen) {
          const priceDetails = this.els.list.querySelector('details[data-price-accordion]')
          if (priceDetails) priceDetails.setAttribute('open', '')
        }

        this.setupEventListeners()
      }

      renderActiveFacets (facets) {
        this.els.activeFilters.toggleAttribute('hidden', facets.length === 0)
        this.els.activeList.innerHTML = ''

        facets.forEach(facet => {
          this.els.activeList.insertAdjacentHTML(
            'beforeend',
            window.theme.results.templates.ActiveFacet(facet),
          )
        })
      }
    },
    { extends: 'form' },
  )
}
